import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import * as OTPAuth from "otpauth";

/**
 * User MFA hook. The TOTP secret is NEVER fetched into the browser after setup.
 * Setup/verify/disable all go through the `mfa-manage` edge function which
 * validates the OTP server-side using the service role.
 */
export function useUserMFA() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const generateMFASecret = useCallback(() => {
    const secret = new OTPAuth.Secret({ size: 20 });
    return secret.base32;
  }, []);

  const generateQRCode = useCallback(
    (secret: string) => {
      const totp = new OTPAuth.TOTP({
        issuer: "PayStream",
        label: user?.email || "user",
        algorithm: "SHA1",
        digits: 6,
        period: 30,
        secret: secret,
      });
      return totp.toString();
    },
    [user?.email],
  );

  const callMfa = async (
    action: "setup" | "verify" | "disable",
    payload: { token: string; secret?: string },
  ) => {
    const { data, error } = await supabase.functions.invoke("mfa-manage", {
      body: { action, ...payload },
    });
    if (error) {
      let msg = error.message || "Request failed";
      try {
        const ctx = (error as any).context;
        if (ctx?.clone) {
          const parsed = await ctx.clone().json();
          if (parsed?.error) msg = parsed.error;
        }
      } catch (_) {
        // ignore
      }
      throw new Error(msg);
    }
    if (!data?.success) {
      throw new Error(data?.error || "Verification failed");
    }
    return data;
  };

  const setupMFA = async (token: string, secret: string) => {
    if (!user) throw new Error("User not authenticated");
    setIsLoading(true);
    try {
      await callMfa("setup", { token, secret });
      toast.success("Two-factor authentication setup completed successfully");
      return { success: true };
    } catch (error: any) {
      console.error("MFA setup error:", error);
      toast.error(error.message || "Failed to setup two-factor authentication");
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const disableMFA = async (token: string) => {
    if (!user) throw new Error("User not authenticated");
    setIsLoading(true);
    try {
      await callMfa("disable", { token });
      toast.success("Two-factor authentication disabled successfully");
      return { success: true };
    } catch (error: any) {
      console.error("MFA disable error:", error);
      toast.error(
        error.message || "Failed to disable two-factor authentication",
      );
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const checkMFAStatus = useCallback(async () => {
    if (!user) return { enabled: false, verified: false };

    try {
      const { data: profile, error } = await supabase
        .from("public_profiles")
        .select("mfa_enabled, last_mfa_verification")
        .eq("id", user.id)
        .single();

      if (error) throw error;

      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const lastVerification = profile?.last_mfa_verification
        ? new Date(profile.last_mfa_verification)
        : null;

      return {
        enabled: profile?.mfa_enabled || false,
        verified: lastVerification
          ? lastVerification > twentyFourHoursAgo
          : false,
      };
    } catch (error) {
      console.error("Error checking MFA status:", error);
      return { enabled: false, verified: false };
    }
  }, [user]);

  const generateBackupCodes = async () => {
    if (!user) throw new Error("User not authenticated");

    setIsLoading(true);
    try {
      // Generate 10 random backup codes
      const backupCodes = Array.from({ length: 10 }, () => {
        return Math.random().toString(36).substring(2, 10).toUpperCase();
      });

      const { error } = await supabase
        .from("profiles")
        .update({
          mfa_backup_codes: backupCodes,
        })
        .eq("id", user.id);

      if (error) throw error;

      toast.success("Backup codes generated successfully");
      return { success: true, codes: backupCodes };
    } catch (error: any) {
      console.error("Backup codes generation error:", error);
      toast.error(error.message || "Failed to generate backup codes");
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyMFA = async (token: string) => {
    if (!user) throw new Error("User not authenticated");
    setIsLoading(true);
    try {
      await callMfa("verify", { token });
      toast.success("Verification successful");
      return { success: true };
    } catch (error: any) {
      console.error("MFA verification error:", error);
      toast.error(error.message || "Two-factor verification failed");
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    generateMFASecret,
    generateQRCode,
    setupMFA,
    verifyMFA,
    disableMFA,
    checkMFAStatus,
    generateBackupCodes,
    isLoading,
  };
}
